import { useState, useEffect, useCallback } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import {
  CalendarPlus, ChevronLeft, Loader2, Building2, User, Trash2,
  Mail, Phone, Globe, MapPin, Send, Pencil,
} from "lucide-react";
import { Badge, Button, Input, Loader, Switch, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from "@wa/ui";
import { resolveCompanyGrant } from "@wa/core/portal-modules";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  type Client, type Contact, type Activity, type ClientStatus,
  type ClientNotificationCategory,
  getClient, getClients, updateClient, deleteClient,
  addClientActivity, updateActivity,
  getClientNotifications, setClientNotifications,
} from "../../../lib/clients";
import { getThreads, type MailThread } from "../../../lib/mail";
import { ActivityList } from "../_shared/ActivityList";
import { NextStepBadge, NextStepDialog } from "../_shared/NextStep";
import { earliestOutstandingAt } from "../../../lib/nextStep";
import { useStaff } from "../_shared/staff";
import { NewClientModal } from "../_shared/NewClientModal";
import { DuplicatesPage } from "./DuplicatesPage";
import { ClientEditModal } from "../_shared/ClientEditModal";
import { Btn, PageHead, SearchField, WebsiteLink } from "../../ui";
import { PhoneLink } from "../../telephony/PhoneLink";
import { ClientsList } from "./ClientsList";
import { ClientTimeline } from "./ClientTimeline";
import { ContactsTab } from "./ContactsTab";
import { PortalTab } from "./PortalTab";
import { ClientLogs } from "./ClientLogs";

type T = ReturnType<typeof useT>;

type Tab = "overview" | "activity" | "contacts" | "portal" | "activities" | "email" | "notifications" | "logs";

function ActivitiesTab({ t, client, canManage, onChange }: {
  t: T; client: Client & { activities: Activity[] }; canManage: boolean; onChange: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [planning, setPlanning] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      await addClientActivity(client.id, { type: "note", body: note.trim() });
      setNote("");
      onChange();
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-3">
      {/* The state of the next contact, and the way to change it, above the
          history that explains both. Read off the timeline this tab already has
          rather than from the list field, which only the register receives. */}
      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Btn onClick={() => setPlanning(true)}>
            <CalendarPlus className="size-4" /> {t("nextStep.plan")}
          </Btn>
          <NextStepBadge nextStepAt={earliestOutstandingAt(client.activities)} />
        </div>
      )}
      {canManage && (
        <form onSubmit={submit} className="flex items-end gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("clients.notePlaceholder")} />
          <Button type="submit" size="sm" disabled={busy || !note.trim()}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
          </Button>
        </form>
      )}
      <ActivityList
        rows={client.activities}
        empty={t("clients.noActivities")}
        onDone={
          canManage
            ? async (a) => {
                await updateActivity(a.id, { doneAt: new Date().toISOString() });
                // Reloaded rather than patched locally: this tab does not own the
                // client record, it is handed one, and `onChange` is the only way
                // it has to make the change stick past a tab switch.
                onChange();
              }
            : undefined
        }
      />

      {planning ? (
        <NextStepDialog
          title={client.name}
          onClose={() => setPlanning(false)}
          onSave={(body) => addClientActivity(client.id, body)}
          onSaved={() => {
            setPlanning(false);
            onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function ClientDetail({ user }: { user: StaffUser }) {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [client, setClient] = useState<(Client & { contacts: Contact[]; activities: Activity[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);
  // Read here rather than inside the modal: the owner picker needs the list the
  // moment the modal opens, and a fetch on open shows an empty dropdown first.
  // The shared directory hands it over already sorted - people who have left the
  // company are last, which is the order any picker of theirs has to use.
  const { staff } = useStaff();
  const canManage = userCanPlatform(user, "manage:crm.clients");
  const canManageNotif = userCanPlatform(user, "manage:crm.notifications");
  // The ledger has a key of its own, so the tab has to be hidden as well as
  // gated. A tab that 403s the moment it is opened reads as a broken page
  // rather than as somebody else's screen.
  const canReadLogs = userCanPlatform(user, "read:crm.logs");

  const reload = useCallback(() => {
    if (!id) return;
    getClient(id).then(setClient);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getClient(id).then(setClient).finally(() => setLoading(false));
  }, [id]);

  async function doDelete() {
    if (!client || !confirm(t("clients.deleteConfirm"))) return;
    await deleteClient(client.id);
    nav("/clients");
  }

  async function setStatus(s: ClientStatus) {
    if (!client) return;
    await updateClient(client.id, { status: s });
    setClient({ ...client, status: s });
  }

  if (loading) return <Loader />;
  if (!client) return <p className="text-muted-foreground">{t("clients.notFound")}</p>;

  // Portal sits next to Contacts because the two are one question asked at two
  // levels: who from this company holds a key, and what the key opens. The
  // contact tab's grant control cannot be read without the company grant this
  // one writes.
  // Logs last, and only for whoever holds `read:crm.logs`. Last because it is
  // the paper trail rather than a working screen: it is opened when a question
  // is asked about the record, not while the record is being worked on.
  const TABS: Tab[] = [
    "overview", "activity", "contacts", "portal", "activities", "email", "notifications",
    ...(canReadLogs ? (["logs"] as Tab[]) : []),
  ];

  /**
   * What this company may open, CRM-wide default already folded in.
   *
   * Resolved once here rather than inside each tab, because it is the level the
   * contact grants are drawn against and the detail route is the only place both
   * halves arrive together. `resolveCompanyGrant` is the single home of the
   * "client REPLACES the default" rule - restating it in a component would be a
   * second copy that nothing can check against the first.
   */
  const companyGrant = resolveCompanyGrant(client.portalDefaults?.modules ?? null, client.portalModules ?? null);

  return (
    <div className="space-y-4">
      <button onClick={() => nav("/clients")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-3.5" /> {t("clients.back")}
      </button>

      <div className="rounded-xl border border-border bg-background p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              {client.kind === "company"
                ? <Building2 className="size-5 text-muted-foreground" />
                : <User className="size-5 text-muted-foreground" />}
              {client.name}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {client.regCode && <Badge variant="outline" className="text-[10px]">{t("clients.regCode")}: {client.regCode}</Badge>}
              {client.vatNumber && <Badge variant="outline" className="text-[10px]">{client.vatNumber}</Badge>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" /> {t("clients.edit")}
              </Button>
            )}
            {canManage && (
              <Select value={client.status} onValueChange={(v) => setStatus(v as ClientStatus)}>
                <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("clients.statusActive")}</SelectItem>
                  <SelectItem value="inactive">{t("clients.statusInactive")}</SelectItem>
                </SelectContent>
              </Select>
            )}
            {canManage && (
              <Button variant="ghost" size="sm" onClick={doDelete} className="text-red-600">
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-1 border-b border-border">
          {TABS.map((tk) => (
            <button
              key={tk}
              onClick={() => setTab(tk)}
              className={cn(
                "relative px-3 py-2 text-sm font-medium transition-colors",
                tab === tk ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`clients.tab.${tk}`)}
              {tab === tk && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "overview" && (
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              {client.email && <div className="inline-flex items-center gap-2"><Mail className="size-4 text-muted-foreground" />{client.email}</div>}
              {client.phone && (
                <div className="inline-flex items-center gap-2">
                  <Phone className="size-4 text-muted-foreground" />
                  <PhoneLink
                    phone={client.phone}
                    source="manual"
                    companyName={client.name}
                    regCode={client.regCode ?? undefined}
                    clientId={client.id}
                    hideIcon
                  />
                </div>
              )}
              {client.website && <div className="inline-flex items-center gap-2"><Globe className="size-4 text-muted-foreground" /><WebsiteLink value={client.website} /></div>}
              {(client.addressLine || client.city) && (
                <div className="inline-flex items-center gap-2"><MapPin className="size-4 text-muted-foreground" />
                  {[client.addressLine, client.city, client.zip].filter(Boolean).join(", ")}
                </div>
              )}
              {client.notes && <p className="sm:col-span-2 rounded-lg bg-muted/50 p-3 text-muted-foreground">{client.notes}</p>}
            </div>
          )}
          {tab === "activity" && <ClientTimeline t={t} clientId={client.id} />}
          {tab === "contacts" && <ContactsTab t={t} client={client} canManage={canManage} companyGrant={companyGrant} onChange={reload} />}
          {tab === "portal" && <PortalTab t={t} client={client} canManage={canManage} onChange={reload} />}
          {tab === "activities" && <ActivitiesTab t={t} client={client} canManage={canManage} onChange={reload} />}
          {tab === "email" && <ClientEmailsTab t={t} clientId={client.id} />}
          {tab === "notifications" && <NotificationsTab t={t} clientId={client.id} canManage={canManageNotif} />}
          {tab === "logs" && <ClientLogs t={t} clientId={client.id} />}
        </div>
      </div>

      {editing && (
        <ClientEditModal
          client={client}
          staff={staff}
          onClose={() => setEditing(false)}
          // The saved row comes back without contacts or activities, so the tabs
          // keep the ones already loaded rather than blanking on every save.
          onSaved={(c) => setClient((prev) => (prev ? { ...prev, ...c } : prev))}
        />
      )}
    </div>
  );
}

// ── Client e-mail (the threads tied to this client) ─────────────────────────────
function ClientEmailsTab({ t, clientId }: { t: ReturnType<typeof useT>; clientId: string }) {
  const [rows, setRows] = useState<MailThread[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getThreads({ clientId, pageSize: 50 })
      .then((r) => setRows(r.rows))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <Loader />;
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <Mail className="mx-auto mb-2 size-6 opacity-40" />
        {t("clients.emailEmpty")}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
      {rows.map((th) => (
        <li key={th.id}>
          <Link to={`/email/${th.id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent/40">
            <Mail className={cn("size-4 shrink-0", th.unread ? "text-primary" : "text-muted-foreground")} />
            <div className="min-w-0 flex-1">
              <p className={cn("truncate text-sm", th.unread ? "font-semibold" : "font-medium")}>{th.subject}</p>
              {th.messages?.[0]?.snippet && <p className="truncate text-xs text-muted-foreground">{th.messages[0].snippet}</p>}
            </div>
            {th.matchedCompanyName && (
              <Badge variant="secondary" className="shrink-0 gap-1 text-[10px]"><Building2 className="size-3" />{th.matchedCompanyName}</Badge>
            )}
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {th.lastMessageAt ? new Date(th.lastMessageAt).toLocaleDateString("et-EE", { day: "2-digit", month: "2-digit" }) : ""}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ── Client notification prefs (e-mail per module; RBAC: manage:crm.notifications) ──
function NotificationsTab({ t, clientId, canManage }: { t: T; clientId: string; canManage: boolean }) {
  const [cats, setCats] = useState<ClientNotificationCategory[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getClientNotifications(clientId).then((r) => setCats(r.categories)).catch(() => setCats([]));
  }, [clientId]);

  function toggle(key: string, val: boolean) {
    setCats((cs) => cs ? cs.map((c) => (c.key === key ? { ...c, enabled: val } : c)) : cs);
    setSaved(false);
  }

  async function save() {
    if (!cats) return;
    setBusy(true); setSaved(false);
    try {
      const r = await setClientNotifications(clientId, cats);
      setCats(r.categories);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally { setBusy(false); }
  }

  if (!cats) return <Loader />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("clients.notif.intro")}</p>
      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
        {cats.map((c) => (
          <li key={c.key} className="flex items-center justify-between gap-4 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">{t(`clients.notif.cat.${c.key}`)}</p>
              <p className="text-xs text-muted-foreground">{t(`clients.notif.catDesc.${c.key}`)}</p>
            </div>
            <Switch checked={c.enabled} disabled={!canManage} onCheckedChange={(v) => toggle(c.key, v)} aria-label={t(`clients.notif.cat.${c.key}`)} />
          </li>
        ))}
      </ul>
      {canManage ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : null} {saved ? t("common.saved") : t("common.save")}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("clients.notif.readonly")}</p>
      )}
    </div>
  );
}

export function ClientsSection({ user }: { user: StaffUser }) {
  return (
    <Routes>
      <Route path="/" element={<ClientsList user={user} />} />
      {/* Above `/:id` so a reader meets the static segment first. React Router
          ranks it that way regardless, but a duplicates screen that quietly
          became "the client whose id is duplicates" would be a long afternoon. */}
      <Route path="/duplicates" element={<DuplicatesPage user={user} />} />
      <Route path="/:id" element={<ClientDetail user={user} />} />
    </Routes>
  );
}
